import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type { HealthStatus, HealthEntry } from "@/types/health-types";

const apiBaseUrl = import.meta.env.VITE_API_URL;
const serverBaseUrl = apiBaseUrl.replace(/\/api$/, "");

// READ — API health status
// Falls back to direct fetch if fetchWrapper fails (health endpoint may not require auth)
export function useApiHealth() {
    const { data: apiHealth, isFetching: loading, error, refetch: refetchApiHealth, dataUpdatedAt } = useQuery({
        queryKey: ["health", "api"],
        queryFn: async () => {
            try {
                return await fetchWrapper.get<HealthStatus>(`${serverBaseUrl}/health`);
            } catch {
                // Fallback to direct fetch with credentials
                const response = await fetch(`${serverBaseUrl}/health`, {
                    credentials: "include",
                    headers: { Accept: "application/json, text/plain, */*" },
                });

                if (response.url.includes("/login") || response.status === 401) {
                    throw new Error("Authentication required");
                }
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const contentType = response.headers.get("content-type");
                if (contentType?.includes("application/json")) {
                    return response.json() as Promise<HealthStatus>;
                }

                // Plain text fallback — API returns "Healthy"/"Unhealthy"
                const text = await response.text();
                return {
                    status: text === "Healthy" ? "Healthy" : "Unhealthy",
                    totalDuration: "00:00:00.0000000",
                    entries: {},
                } as HealthStatus;
            }
        },
        staleTime: 0,
    });

    const apiHealthEntries = computed<[string, HealthEntry][]>(() =>
        apiHealth.value?.entries
            ? (Object.entries(apiHealth.value.entries) as [string, HealthEntry][])
            : []
    );

    return { apiHealth, apiHealthEntries, loading, error: error as Ref<ApiError | null>, refetchApiHealth, dataUpdatedAt };
}
